import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWandMagicSparkles, 
  faSpinner, 
  faCheckCircle,
  faExclamationCircle,
  faClock,
  faPlay,
  faStar,
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import styles from './PreviewTab.module.css';
import { 
  PreviewOption, 
  PreviewMetricsData,
  startPreviewGeneration,
  getPreviewJobStatus,
  getPreviewMetrics,
  clearPreviewData
} from '../../../services/videoPreviewService';

interface PreviewTabProps {
  projectId: string;
  onPreviewSelected: (option: PreviewOption) => void;
  onClearPreview: () => void;
  onPreviewGenerationComplete?: () => void; // NEW: Callback to reload media items
}

const PreviewTab: React.FC<PreviewTabProps> = ({ 
  projectId,
  onPreviewSelected,
  onClearPreview,
  onPreviewGenerationComplete // NEW
}) => {
  const { t } = useTranslation('videoEditor');
  const [isGenerating, setIsGenerating] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [previewOptions, setPreviewOptions] = useState<PreviewOption[]>([]);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [metricsData, setMetricsData] = useState<PreviewMetricsData | null>(null);

  // Poll for existing metrics on mount
  useEffect(() => {
    fetchExistingMetrics();
  }, [projectId]);

  // Poll job status when generating
  useEffect(() => {
    if (jobId && isGenerating) {
      const interval = setInterval(pollJobStatus, 2000);
      return () => clearInterval(interval);
    }
  }, [jobId, isGenerating]);

  const fetchExistingMetrics = async () => {
    try {
      const data = await getPreviewMetrics(projectId);
      if (data) {
        setMetricsData(data);
        setPreviewOptions(data.options);
        // Don't auto-select - let user choose
        // setSelectedOptionId(data.contentAnalysis.recommendedOption);
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
    }
  };

  const pollJobStatus = async () => {
    if (!jobId) return;
    
    try {
      const status = await getPreviewJobStatus(jobId);
      setProgress(status.percentage);
      setStatusMessage(status.message);
      
      if (status.status === 'completed') {
        setIsGenerating(false);
        await fetchExistingMetrics();
        
        // NEW: Trigger reload of media items in parent component
        if (onPreviewGenerationComplete) {
          console.log('🎬 Preview generation complete - triggering media reload');
          onPreviewGenerationComplete();
        }
      } else if (status.status === 'failed') {
        setIsGenerating(false);
        setError(status.error || 'Preview generation failed');
      }
    } catch (err: any) {
      console.error('Error polling status:', err);
      setError(err.message || 'Failed to check status');
      setIsGenerating(false);
    }
  };

  const handleGeneratePreview = async () => {
    setIsGenerating(true);
    setError(null);
    setProgress(0);
    setStatusMessage(t('videoEditor.preview.initializing'));
    
    // Hide old preview options while regenerating
    setPreviewOptions([]);
    setSelectedOptionId(null);
    setMetricsData(null);
    
    // Clear any active preview on timeline
    onClearPreview();

    try {
      const response = await startPreviewGeneration({ projectId });
      setJobId(response.jobId);
    } catch (err: any) {
      setError(err.message || t('videoEditor.preview.generateFailed'));
      setIsGenerating(false);
    }
  };

  const handleRemoveAllPreviews = async () => {
    if (!window.confirm(t('videoEditor.preview.removeConfirm'))) {
      return;
    }

    try {
      await clearPreviewData(projectId);
      setPreviewOptions([]);
      setSelectedOptionId(null);
      setMetricsData(null);
      
      // Clear preview filter to show originals
      onPreviewSelected({
        id: 'clear-preview',
        name: t('videoEditor.preview.clearPreviewName'),
        description: t('videoEditor.preview.clearPreviewDesc'),
        metrics: { engagement: 0, tension: 0, clarity: 0, pacing: 0, retention: 0 }
      });
      
      // Reload page to refresh media items without previews
      window.location.reload();
    } catch (err: any) {
      setError(err.message || t('videoEditor.preview.removeFailed'));
    }
  };

  const handleSelectOption = (option: PreviewOption) => {
    setSelectedOptionId(option.id);
    onPreviewSelected(option);
  };

  const getMetricLevel = (value: number): 'high' | 'medium' | 'low' => {
    if (value >= 80) return 'high';
    if (value >= 60) return 'medium';
    return 'low';
  };

  return (
    <div className={styles.previewContainer}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.title}>
          <FontAwesomeIcon icon={faWandMagicSparkles} />
          {t('videoEditor.preview.title')}
        </div>
        <div className={styles.description}>
          {t('videoEditor.preview.description')}
        </div>
      </div>

      {/* Generate Button */}
      {!previewOptions.length && !isGenerating && (
        <button
          className={styles.generateButton}
          onClick={handleGeneratePreview}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin />
              {t('videoEditor.preview.generating')}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faWandMagicSparkles} />
              {t('videoEditor.preview.generateButton')}
            </>
          )}
        </button>
      )}

      {/* Progress Section */}
      {isGenerating && (
        <div className={styles.progressSection}>
          <div className={styles.progressText}>
            <FontAwesomeIcon icon={faSpinner} spin />
            {statusMessage}
          </div>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className={`${styles.statusBadge} ${styles.error}`}>
          <FontAwesomeIcon icon={faExclamationCircle} />
          {error}
        </div>
      )}

      {/* Preview Options */}
      {previewOptions.length > 0 && (
        <>
          {/* Success Badge */}
          <div className={`${styles.statusBadge} ${styles.success}`}>
            <FontAwesomeIcon icon={faCheckCircle} />
            {t('videoEditor.preview.optionsGenerated', { count: previewOptions.length })}
          </div>

          {/* Themes Info */}
          {metricsData && (
            <div className={styles.description}>
              <strong>{t('videoEditor.preview.dominantThemes')}:</strong> {metricsData.contentAnalysis.dominantThemes.join(', ')}
              <br />
              <strong>{t('videoEditor.preview.suggestedDuration')}:</strong> {metricsData.contentAnalysis.suggestedDuration}s
            </div>
          )}

          {/* Options List */}
          <div className={styles.optionsList}>
            {previewOptions.map((option) => (
              <div
                key={option.id}
                className={`${styles.optionCard} ${
                  selectedOptionId === option.id ? styles.selected : ''
                }`}
                onClick={() => handleSelectOption(option)}
              >
                <div className={styles.optionHeader}>
                  <div className={styles.optionName}>{option.name}</div>
                  {metricsData?.contentAnalysis.recommendedOption === option.id && (
                    <div className={styles.recommendedBadge}>
                      <FontAwesomeIcon icon={faStar} /> {t('videoEditor.preview.recommended')}
                    </div>
                  )}
                </div>

                <div className={styles.optionDescription}>
                  {option.description}
                </div>

                {/* Metrics Grid */}
                <div className={styles.metricsGrid}>
                  {Object.entries(option.metrics).map(([key, value]) => (
                    <div key={key} className={styles.metricItem}>
                      <div className={styles.metricLabel}>{key}</div>
                      <div className={styles.metricValue}>{value}%</div>
                      <div className={styles.metricBar}>
                        <div
                          className={`${styles.metricBarFill} ${
                            styles[getMetricLevel(value)]
                          }`}
                          style={{ width: `${value}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <button
              className={`${styles.actionButton} ${styles.primary}`}
              onClick={handleGeneratePreview}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  {t('videoEditor.preview.generating')}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faWandMagicSparkles} />
                  {t('videoEditor.preview.regenerate')}
                </>
              )}
            </button>
            {selectedOptionId && (
              <button
                className={styles.actionButton}
                onClick={() => {
                  setSelectedOptionId(null);
                  onClearPreview(); // Call the clear handler
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
                {t('videoEditor.preview.clearPreview')}
              </button>
            )}
            <button
              className={`${styles.actionButton} ${styles.danger}`}
              onClick={handleRemoveAllPreviews}
            >
              <FontAwesomeIcon icon={faTrash} />
              {t('videoEditor.preview.removeAll')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PreviewTab;
